import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Minus, X, Clock } from 'lucide-react';
import { useSeo, Reveal, DemoButton, Eyebrow, H2 } from './site/ui';
import { Faq, Closing } from './site/HomeSections2';
import { ROWS, DAY, SAVINGS, SWITCH, FAQ } from './site/compare';

const COLS = ['Paper', 'Excel', 'Driveline'];
const MARK = {
  yes: <Check size={16} className="shrink-0 text-[#0f766e]" aria-label="Yes" />,
  part: <Minus size={16} className="shrink-0 text-[#b45309]" aria-label="Partly" />,
  no: <X size={16} className="shrink-0 text-[#be123c]" aria-label="No" />,
};

const Hero = () => (
  <section className="mx-auto max-w-7xl px-5 pb-14 pt-32 sm:px-8" data-testid="compare-hero">
    <Reveal>
      <Eyebrow>Driveline vs paper and spreadsheets</Eyebrow>
      <h1 className="dl-display mt-4 max-w-4xl text-4xl font-semibold leading-[1.04] tracking-tight text-[#0f172a] sm:text-5xl lg:text-6xl" data-testid="compare-title">
        The bill book and the Excel sheet got you here. They won’t get you to ten bays.
      </h1>
      <p className="mt-5 max-w-2xl text-base text-[#475569] md:text-lg">Here is how a workshop day looks on paper, in a spreadsheet and in Driveline, task by task.</p>
      <DemoButton className="mt-8" testid="compare-hero-book-demo" />
    </Reveal>
    <div className="mt-12 grid gap-3 sm:grid-cols-3" data-testid="compare-savings">
      {SAVINGS.map(([n, l], i) => (
        <Reveal key={l} delay={i * 90} className="rounded-3xl bg-[#ffffff] p-6">
          <p className="dl-display text-3xl font-semibold text-[#0f766e]">{n}</p>
          <p className="mt-1 text-sm text-[#334155]">{l}</p>
        </Reveal>
      ))}
    </div>
    <p className="mt-3 text-xs text-[#64748b]" data-testid="compare-estimate-note">Estimates for a typical 3–5 bay workshop. Your numbers depend on how you work today.</p>
  </section>
);

const Cell = ({ c, hi }) => (
  <td className={`px-4 py-4 align-top ${hi ? 'bg-[#f0fdfa]' : ''}`}>
    <span className="flex gap-2 text-[13px] leading-snug text-[#334155]">{MARK[c[0]]}{c[1]}</span>
  </td>
);

const Table = () => (
  <section className="bg-[#efece4] py-20 sm:py-28" data-testid="compare-table-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Eyebrow>Side by side</Eyebrow>
      <H2>12 jobs every workshop does.</H2>
      <div className="mt-10 overflow-x-auto rounded-3xl border border-[#e2e0d8] bg-[#ffffff]">
        <table className="w-full min-w-[820px] text-left" data-testid="compare-table">
          <thead>
            <tr className="border-b border-[#e2e0d8]">
              <th className="sticky left-0 z-10 bg-[#ffffff] px-4 py-4 text-xs font-bold uppercase tracking-wider text-[#64748b]">Task</th>
              {COLS.map((c, i) => <th key={c} className={`px-4 py-4 text-sm font-semibold ${i === 2 ? 'bg-[#0f766e] text-[#ffffff]' : 'text-[#0f172a]'}`}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([task, ...cells], r) => (
              <tr key={task} className="border-b border-[#f1efe9] last:border-0" data-testid={`compare-row-${r}`}>
                <th className="sticky left-0 z-10 bg-[#ffffff] px-4 py-4 align-top text-sm font-semibold text-[#0f172a]">{task}</th>
                {cells.map((c, i) => <Cell key={COLS[i]} c={c} hi={i === 2} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

const Day = () => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="compare-day">
    <Eyebrow>A day at the workshop</Eyebrow>
    <H2>Before and after.</H2>
    <ol className="mt-10 space-y-3">
      {DAY.map(([t, what, before, after], i) => (
        <Reveal as="li" key={t} delay={i * 80} className="grid gap-3 rounded-3xl bg-[#ffffff] p-5 md:grid-cols-[150px_1fr_1fr]" data-testid={`compare-day-${i}`}>
          <div><p className="dl-display inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f766e]"><Clock size={14} />{t}</p><p className="mt-1 font-semibold text-[#0f172a]">{what}</p></div>
          <p className="rounded-2xl bg-[#fff1f2] p-4 text-sm text-[#475569]"><b className="block text-xs uppercase tracking-wider text-[#be123c]">Before</b>{before}</p>
          <p className="rounded-2xl bg-[#f0fdfa] p-4 text-sm text-[#334155]"><b className="block text-xs uppercase tracking-wider text-[#0f766e]">With Driveline</b>{after}</p>
        </Reveal>
      ))}
    </ol>
  </section>
);

const Switch = () => (
  <section className="bg-[#0b1220] py-20 sm:py-28" data-testid="compare-switch">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-2">
      <div><Eyebrow dark>Switching</Eyebrow><H2 dark>Switching takes one afternoon.</H2><p className="mt-4 max-w-md text-[#94a3b8]">We set it up with you on a call. Your first GST invoice goes out the same day.</p>
        <Link to="/driveline/switch" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5eead4] hover:underline" data-testid="compare-switch-link">Switching from another software? We move your data free →</Link></div>
      <ol className="space-y-3">
        {SWITCH.map((s, i) => (
          <Reveal as="li" key={s} delay={i * 80} className="flex items-center gap-4 rounded-2xl border border-[#1e293b] p-4" data-testid={`compare-switch-${i}`}>
            <span className="dl-display flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#5eead4] text-sm font-semibold text-[#0b1220]">{i + 1}</span>
            <span className="text-[15px] text-[#e2e8f0]">{s}</span>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export default function DrivelineCompare() {
  useSeo({ title: 'Driveline vs paper and Excel · Workshop software comparison', description: 'Paper job cards, bill books and Excel sheets compared with Driveline, task by task: GST invoices, parts stock, payroll, reminders and reviews.' });
  return (
    <div data-testid="compare-page">
      <Hero />
      <Table />
      <Day />
      <Switch />
      <Faq items={FAQ} title="Questions before switching." />
      <Closing title="See how your workshop day looks on Driveline." />
    </div>
  );
}
